import React, { useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { css, cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import Spinner from '@atlaskit/spinner/spinner';
import { token } from '@atlaskit/tokens';

import { ObservableSpinner } from '@confluence/infinite-scroll';

import type { AtAGlanceSummaries } from '../../at-a-glance/buildAtAGlanceSummaries.ts';
import { BylineConnectionIconStack } from '../../BylineConnectionIconStack.tsx';
import { ConnectionsByLineMenuItem, type LinkDirection } from '../../ConnectionsByLineMenuItem.tsx';
import type { ConnectionRowInteractionHandler } from '../../utils/connectionRowActionTypes.ts';
import type { EnhancedSmartLink } from '../../utils/smartLinkTypes.ts';

import { ConnectionsByLineSectionShell } from './ConnectionsByLineSectionShell.tsx';

// Wrap each rendered row so its own hover / selected background clips to the
// same 12px radius as the section card. Overflow-hidden confines whatever
// paint the row's internals apply.
const roundedRowStyles = css({
	borderRadius: token('radius.large'),
	overflow: 'hidden',
});

const styles = cssMap({
	// Matches ObservableSpinner's footprint, so swapping the two does not shift the list.
	loadingMore: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '40px',
	},
	// The body's only child when the section has no rows to show.
	emptyMessage: {
		font: token('font.body'),
		color: token('color.text.disabled'),
		textAlign: 'center',
	},
});

export type SectionPagination = {
	hasNextPage: boolean;
	/** Advances with each page; re-arms the sentinel. */
	endCursor: string | null;
	/** True while a page is in flight, when `onLoadMore` refuses to act. */
	isLoading: boolean;
	onLoadMore: () => void;
};

export type ConnectionsByLineSectionProps = {
	/** Section title shown in the header. */
	title: string;
	/** Optional icon shown to the left of the title in the header. */
	iconBefore?: ReactNode;
	/** Smart-link items rendered as `ConnectionsByLineMenuItem` rows in the body. */
	items: EnhancedSmartLink[];
	/** Forwarded to each row — controls click behaviour (scroll vs open new tab). */
	linkDirection: LinkDirection;
	/**
	 * cc-smarts one-liners keyed by ARI. Each row is handed the one written for its
	 * link, if there is one; links the request never described simply have none.
	 */
	aiSummaries?: AtAGlanceSummaries;
	/** Called before a row opens its Smart Link preview. */
	onPreviewOpen?: () => void;
	/** Switches the Teamwork Graph modal to Rovo chat. */
	onAskRovo?: (prompt?: string) => void;
	/** Forwarded to each row: called on a body click or a row action. Keep it stable. */
	onRowInteraction?: ConnectionRowInteractionHandler;
	/**
	 * Shown in the expanded body in place of rows when there are none. Withheld while a paged
	 * section is still loading or has pages left, since the rows may yet arrive.
	 */
	emptyMessage?: ReactNode;
	/**
	 * Header count. Defaults to `items.length`; a paged section passes its own. `null` renders no
	 * badge at all, for a section that does not yet know its count.
	 */
	count?: number | null;
	/** Renders the count as `(N+)`. */
	hasMore?: boolean;
	/** Opt in to scroll pagination. Omit for a section that has all of its items. */
	pagination?: SectionPagination;
	/** Controlled expanded state, forwarded to the shell. Omit to let the shell own it. */
	isExpanded?: boolean;
	/** Called on a header click when `isExpanded` is controlled. */
	onToggleExpanded?: () => void;
	/** Optional test id set on the section wrapper. */
	testId?: string;
};

/**
 * A reusable expandable section rendered as a bordered card with an
 * icon + title + count header and a list of `ConnectionsByLineMenuItem`
 * rows in the body. `BylineConnectionIconStack` summarises the items to the
 * right of the title, shown in both collapsed and expanded states.
 *
 * Both the count and the icon stack are derived from `items` — pass the exact
 * items you want summarised. Smart-link sections start collapsed; the header
 * button toggles the expanded state internally. The shell's `defaultExpanded`
 * is how "At a glance" opens already expanded.
 *
 * With no rows to show, the expanded body renders `emptyMessage` instead, once nothing
 * more can arrive: a paged section withholds it while loading or while pages remain.
 *
 * Row-level affordances are row-owned: each row derives its own actions (such as
 * "Open preview") from its link and renders them as icon buttons on hover, since
 * what a link affords depends on the link rather than on the section it sits in.
 *
 * Pass `pagination` and the section appends a load-more sentinel to the body and
 * calls `onLoadMore` when it scrolls into view; the data container only has to
 * forward its cursor and loading state.
 *
 * This is the smart-link binding of `ConnectionsByLineSectionShell`. Sections whose
 * rows are not smart links use that shell directly.
 */
export const ConnectionsByLineSection = ({
	title,
	iconBefore,
	items,
	linkDirection,
	aiSummaries,
	onPreviewOpen,
	onAskRovo,
	onRowInteraction,
	emptyMessage,
	count = items.length,
	hasMore,
	pagination,
	isExpanded,
	onToggleExpanded,
	testId,
}: ConnectionsByLineSectionProps): React.JSX.Element => {
	// The body only exists while expanded, so the scroll root is held in state to
	// re-run the observer once the body mounts.
	const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);

	// `InfiniteScroll` rebuilds its observer whenever these options change identity.
	const observerOptions = useMemo(
		() => ({ root: scrollRoot, rootMargin: '0px', threshold: 0.05 }),
		[scrollRoot],
	);

	const loadingMoreTestId = testId ? `${testId}--loading-more` : undefined;

	const isEmpty = items.length === 0 && !pagination?.isLoading && !pagination?.hasNextPage;

	return (
		<ConnectionsByLineSectionShell
			title={title}
			count={count ?? undefined}
			hasMore={hasMore}
			iconBefore={iconBefore}
			trailing={<BylineConnectionIconStack links={items} />}
			bodyRef={setScrollRoot}
			isExpanded={isExpanded}
			onToggleExpanded={onToggleExpanded}
			testId={testId}
		>
			{isEmpty && emptyMessage ? (
				<Box xcss={styles.emptyMessage} testId={testId ? `${testId}--empty` : undefined}>
					{emptyMessage}
				</Box>
			) : null}
			{items.map((item) => (
				<div key={`${item.url}-${item.occurrenceIndex}`} css={roundedRowStyles}>
					<ConnectionsByLineMenuItem
						item={item}
						linkDirection={linkDirection}
						aiSummary={item.ari ? aiSummaries?.get(item.ari) : undefined}
						onPreviewOpen={onPreviewOpen}
						onAskRovo={onAskRovo}
						onInteraction={onRowInteraction}
					/>
				</div>
			))}
			{pagination?.hasNextPage ? (
				// Mounted only while `onLoadMore` can act on it: `InfiniteScroll` marks a cursor
				// as used before calling back and ignores that the call was rejected, so a
				// sentinel left mounted during a fetch burns the next cursor and pagination
				// stops. Remounting resets that state.
				pagination.isLoading ? (
					<Box xcss={styles.loadingMore}>
						<Spinner size="small" testId={loadingMoreTestId} />
					</Box>
				) : (
					<ObservableSpinner
						cursor={pagination.endCursor}
						onScrollTo={pagination.onLoadMore}
						observerOptions={observerOptions}
						spinnerSize="small"
						testId={loadingMoreTestId ?? 'connections-by-line-section-loading-more'}
					/>
				)
			) : null}
		</ConnectionsByLineSectionShell>
	);
};
